import React, { useMemo } from 'react';
import { Building, Home } from 'lucide-react';
import { SectionStatus, UnitType } from '@/shared/types';
import { useI18n } from '@/shared/i18n';
import { useSection } from '@/app/providers';
import { SearchableSelect } from '@/core/ui/components';
import { Input } from '../atoms/Input';

export type UnitFormState = {
  type: UnitType;
  sectionId?: string | null;
  tower?: string;
  floor?: string;
  number?: string;
  street?: string;
  exteriorNumber?: string;
  interiorNumber?: string;
  block?: string;
};

interface Props {
  value: UnitFormState;
  onChange: (next: UnitFormState) => void;
  disabled?: boolean;
}

export function buildUnitName(form: UnitFormState): string {
  const base =
    form.type === UnitType.APARTMENT
      ? [form.tower, form.number].filter(Boolean).join(' - ')
      : [form.street, form.exteriorNumber].filter(Boolean).join(' ');
  const interior = form.interiorNumber ? `Int. ${form.interiorNumber}` : '';
  return [base, interior].filter(Boolean).join(' ').trim();
}

/**
 * Con al menos una sección activa, toda unidad debe pertenecer a una: el
 * backend rechaza crearla o desasignarla sin sección (400 SECT_1012).
 */
export function useUnitSectionRequired(): boolean {
  const { sections } = useSection();
  return sections.some((section) => section.status === SectionStatus.ACTIVE);
}

export const UnitFormFields: React.FC<Props> = ({ value, onChange, disabled }) => {
  const { t } = useI18n();
  const { sections, hasSections, isSectionAdmin } = useSection();

  const sectionOptions = useMemo(
    () =>
      sections
        .filter((section) => section.status === SectionStatus.ACTIVE)
        .map((section) => ({ value: section.id, label: section.name })),
    [sections],
  );
  const sectionRequired = sectionOptions.length > 0;

  const handleSectionChange = (sectionId: string | null) => {
    const section = sections.find((s) => s.id === sectionId);
    onChange({
      ...value,
      sectionId: sectionId ?? null,
      // §6.4: `tower` se deriva de la sección, pero solo en departamentos: una
      // casa se identifica por calle y número, no tiene torre.
      tower: value.type === UnitType.APARTMENT ? (section?.name ?? value.tower) : value.tower,
    });
  };

  const handleTypeChange = (type: UnitType) => {
    if (type === UnitType.APARTMENT) {
      // Al volver a departamento se recupera la torre desde la sección elegida.
      const section = sections.find((s) => s.id === value.sectionId);
      onChange({
        ...value,
        type,
        street: '',
        exteriorNumber: '',
        tower: section?.name ?? value.tower,
      });
    } else {
      onChange({ ...value, type, tower: '', floor: '', number: '' });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">{t('admin.units.type')}</label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleTypeChange(UnitType.APARTMENT)}
            className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
              value.type === UnitType.APARTMENT
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-bold shadow-sm'
                : 'border-slate-200 text-slate-500 hover:bg-slate-50'
            }`}
          >
            <Building size={24} />
            {t('admin.units.apt')}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleTypeChange(UnitType.HOUSE)}
            className={`p-3 rounded-lg border flex flex-col items-center gap-2 transition-all ${
              value.type === UnitType.HOUSE
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-bold shadow-sm'
                : 'border-slate-200 text-slate-500 hover:bg-slate-50'
            }`}
          >
            <Home size={24} />
            {t('admin.units.house')}
          </button>
        </div>
      </div>

      {hasSections && (
        <div>
          <SearchableSelect
            label="Sección"
            options={sectionOptions}
            value={value.sectionId ?? null}
            onChange={handleSectionChange}
            placeholder={sectionRequired ? 'Selecciona una sección' : 'Sin sección'}
            isSearchable={false}
            isDisabled={disabled}
            // No se puede desasignar si la sección es obligatoria (400 SECT_1012)
            // ni siendo admin de sección (403 SECT_1006).
            isClearable={!sectionRequired && !isSectionAdmin}
          />
          <p className="mt-1 text-xs text-slate-500">
            {value.sectionId
              ? 'Los residentes de esta unidad pasan a la sección automáticamente.'
              : sectionRequired
                ? 'Obligatoria: elige la sección de la unidad.'
                : 'Sin sección: sus residentes no verán el contenido de ninguna sección.'}
          </p>
        </div>
      )}

      <div>
        <span className="block text-sm font-medium text-slate-700 mb-1">{t('common.name')}</span>
        <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          {buildUnitName(value) || '—'}
        </p>
      </div>

      {value.type === UnitType.APARTMENT ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('common.tower')}
              value={value.tower ?? ''}
              onChange={(e) => onChange({ ...value, tower: e.target.value })}
              // La torre se deriva de la sección (§6.4), pero solo mientras hay
              // sección: al dejar la unidad sin asignar nadie la alimenta y el
              // campo tiene que volver a ser editable, o queda un valor
              // obligatorio, heredado de la sección anterior y bloqueado.
              disabled={disabled || (hasSections && !!value.sectionId)}
              required
            />
            <Input
              label={t('common.floor')}
              value={value.floor ?? ''}
              onChange={(e) => onChange({ ...value, floor: e.target.value })}
              disabled={disabled}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('common.unit')}
              value={value.number ?? ''}
              onChange={(e) => onChange({ ...value, number: e.target.value })}
              placeholder="e.g. 402, PH-01"
              disabled={disabled}
              required
            />
            <Input
              label={t('common.int_num')}
              value={value.interiorNumber ?? ''}
              onChange={(e) => onChange({ ...value, interiorNumber: e.target.value })}
              disabled={disabled}
            />
          </div>
        </>
      ) : (
        <>
          <Input
            label={t('common.street')}
            value={value.street ?? ''}
            onChange={(e) => onChange({ ...value, street: e.target.value })}
            disabled={disabled}
            required
          />
          <div className="grid grid-cols-3 gap-4">
            <Input
              label={t('common.ext_num')}
              value={value.exteriorNumber ?? ''}
              onChange={(e) => onChange({ ...value, exteriorNumber: e.target.value })}
              disabled={disabled}
              required
            />
            <Input
              label={t('common.int_num')}
              value={value.interiorNumber ?? ''}
              onChange={(e) => onChange({ ...value, interiorNumber: e.target.value })}
              disabled={disabled}
            />
            <Input
              label={t('common.block')}
              value={value.block ?? ''}
              onChange={(e) => onChange({ ...value, block: e.target.value })}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </div>
  );
};
